import moment from 'moment';
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { ICampaign, IDevice, IUser } from '../../interfaces';
import { arrayEqual } from '../../utils';
import {
    generateDateBlocksForCampaigns,
    getCurrentCampaignBlock,
    getMagsNumberFromDevices
} from '../../utils/campaign-viewer';
import { downloadScreenList, getNewPlanningAfterHoursRemoval, updateCampaignDevices } from '../../utils/pricing';
import { getHoursSumFromPlanning } from '../../utils/weeks-viewer';
import { ChartConflicts } from '../Charts';
import { ConflictsScreenView } from './ConflictScreenView';

const PlanningImpl = (props: {
    campaigns: ICampaign[];
    devices: {
        [key: number]: IDevice;
    };
    setPlanning: (planning: number[][]) => void;
    updateCampaign: (campaign: ICampaign) => void;
    onRemoveDevices: (devices: number[]) => void;
    user: IUser;
}) => {
    const [state, setState] = useState({
        excludedId: new Set<string>(),
        devicesShown: new Array<number>(),
        showOnlyDevicesCampaigns: false
    });

    let campaigns = props.campaigns.filter((c) => !state.excludedId.has(c.id));
    const campRef = campaigns[0];

    if (state.showOnlyDevicesCampaigns) {
        campaigns = campaigns.filter((c, i) => {
            if (i === 0) {
                return true;
            }

            for (const d of campRef.devices) {
                if (c.devices.indexOf(d) !== -1) {
                    return true;
                }
            }

            return false;
        });
    }

    const time_conflicts: {
        start: Date;
        end: Date;
        campaign: ICampaign;
    }[] = [];

    // Sort time conflicts
    time_conflicts.sort((a, b) => a.start.getTime() - b.start.getTime());

    const data: { [key: string]: number[][] } = generateDateBlocksForCampaigns(
        campaigns.slice(1),
        campRef.startDate,
        campRef.endDate,
        time_conflicts
    );

    const currentCampaignBlock = getCurrentCampaignBlock(time_conflicts, campRef);

    return (
        <div className="planning">
            <ChartConflicts campaigns={campaigns} currentCampaignBlock={currentCampaignBlock} data={data} />
            <div className="timeline-data">
                <div>
                    <h4>Campaignes</h4>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Dates</th>
                                <th>Ecrans</th>
                                <th>Magasins</th>
                                <th>Heures / semaine</th>
                                <th>Actif</th>
                                <th>Conflits</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                props.campaigns[0],
                                ...props.campaigns.slice(1).sort((a, b) => {
                                    if (a.state === 'draft') {
                                        return -1;
                                    }

                                    return 0;
                                })
                            ].map((c, i) => {
                                const screens_overflowing = new Set<number>();
                                const overflows = currentCampaignBlock.filter((b) => b.devicesOverflowing.length > 0);
                                for (const o of overflows) {
                                    for (const device_id of o.devicesOverflowing) {
                                        screens_overflowing.add(device_id);
                                    }
                                }

                                const screens_remove_potential = screens_overflowing;
                                const campaign_screens = new Set<number>(c.devices);

                                const intersection = new Set(
                                    [...screens_remove_potential].filter((x) => campaign_screens.has(x))
                                );

                                return (
                                    <tr key={c.id}>
                                        <td>
                                            {i !== 0 || '* '}
                                            {c.name}
                                        </td>
                                        <td>
                                            {moment(c.startDate).format('DD/MM/yy')} -{' '}
                                            {moment(c.endDate).format('DD/MM/yy')}
                                        </td>
                                        <td>{c.devices.length}</td>
                                        <td>{getMagsNumberFromDevices(c.devices, props.devices)}</td>
                                        <td>{getHoursSumFromPlanning(c.planning)}</td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                disabled={c.state !== 'draft' || i === 0}
                                                checked={!state.excludedId.has(c.id)}
                                                onChange={(e) => {
                                                    const s = new Set(state.excludedId);

                                                    if (e.target.checked) {
                                                        s.delete(c.id);
                                                    } else {
                                                        s.add(c.id);
                                                    }

                                                    setState({
                                                        ...state,
                                                        excludedId: s
                                                    });
                                                }}
                                            />
                                        </td>
                                        <td>
                                            {i === 0 || intersection.size === 0 || (
                                                <button
                                                    className="btn"
                                                    onClick={() => {
                                                        props.onRemoveDevices([...intersection]);
                                                    }}
                                                >
                                                    Retirer {intersection.size} de la sél.
                                                </button>
                                            )}
                                            {![1, 2, 4].includes(props.user.id) || i === 0 || intersection.size === 0 || (
                                                <button
                                                    className="btn"
                                                    onClick={() =>
                                                        Swal.fire({
                                                            title: 'Attention',
                                                            text: `Cette opération modifiera une campagne ACTUELLE OU EN COURS ! ${intersection.size} écrans seront ENLEVES DEFINITIVEMENT de cette campagne. Voulez vous continuer?`,
                                                            icon: 'warning',
                                                            confirmButtonText: 'Je comprends ce que je fais',
                                                            cancelButtonText: 'Annuler',
                                                            showCancelButton: true,
                                                            reverseButtons: true
                                                        }).then((res) => {
                                                            if (res.isConfirmed) {
                                                                Swal.fire({
                                                                    text: `Entrez le nom de la campagne (${c.name.trim()}) pour confirmer l'opération`,
                                                                    input: 'text',
                                                                    confirmButtonText: 'Retirer les écrans',
                                                                    cancelButtonText: 'Annuler',
                                                                    showCancelButton: true
                                                                }).then(async (res) => {
                                                                    if (res.isConfirmed) {
                                                                        if (res.value === c.name.trim()) {
                                                                            const devices = c.devices.filter(
                                                                                (id) => !intersection.has(id)
                                                                            );

                                                                            const id = c.id;
                                                                            await updateCampaignDevices(id, [
                                                                                ...intersection
                                                                            ]);
                                                                            for (const campId of c.groupedIds) {
                                                                                await updateCampaignDevices(campId, [
                                                                                    ...intersection
                                                                                ]);
                                                                            }

                                                                            props.updateCampaign({ ...c, devices });
                                                                            Swal.fire('Confirmé');
                                                                        } else {
                                                                            Swal.fire('Nom invalide');
                                                                        }
                                                                    } else {
                                                                        Swal.fire('Opération annulée');
                                                                    }
                                                                });
                                                            }
                                                        })
                                                    }
                                                >
                                                    <span className="material-icons">warning</span> Retirer{' '}
                                                    {intersection.size} écrans
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="conflicts">
                    <h4>Conflits</h4>
                    <div>
                        <table className="table table-conflicts">
                            <thead>
                                <tr>
                                    <th>Dates</th>
                                    <th>Nombre d'écrans</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentCampaignBlock
                                    .filter((b) => b.devicesOverflowing.length > 0)
                                    .map((b) => (
                                        <tr key={b.start.getTime()}>
                                            <td>
                                                {moment(b.start).format('DD/MM HH:mm')}
                                                {' - '}
                                                {moment(b.end).format('DD/MM HH:mm')}
                                            </td>
                                            <td>{b.devicesOverflowing.length}</td>
                                            <td>
                                                <button
                                                    className="btn"
                                                    onClick={() => props.onRemoveDevices(b.devicesOverflowing)}
                                                >
                                                    Retirer les écrans
                                                </button>
                                                <button
                                                    className="btn"
                                                    onClick={() =>
                                                        props.setPlanning(
                                                            getNewPlanningAfterHoursRemoval(
                                                                campRef.planning,
                                                                b.start,
                                                                b.end
                                                            )
                                                        )
                                                    }
                                                >
                                                    Retirer la tranche horaire
                                                </button>
                                                <button
                                                    className="btn"
                                                    onClick={() =>
                                                        downloadScreenList(
                                                            b.devicesOverflowing.map((i) => props.devices[i]),
                                                            [],
                                                            `campaign-${campRef.name}-conflicts-${moment(b.start).format('DD-MM HH-mm')}.xlsx`
                                                        )
                                                    }
                                                >
                                                    Télécharger la liste
                                                </button>
                                                <button
                                                    className="btn"
                                                    onClick={() =>
                                                        setState({ ...state, devicesShown: b.devicesOverflowing })
                                                    }
                                                >
                                                    Voir les écrans
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="options">
                    <h4>Options</h4>
                    <label style={{ marginTop: 32 }}>Afficher uniquement les campagnes relatives à la selection</label>
                    <input
                        type="checkbox"
                        checked={state.showOnlyDevicesCampaigns}
                        onChange={(e) =>
                            setState((prevState) => ({
                                ...prevState,
                                showOnlyDevicesCampaigns: e.target.checked
                            }))
                        }
                    />
                </div>
            </div>
            {state.devicesShown.length === 0 || (
                <ConflictsScreenView
                    devices={state.devicesShown.map((id) => props.devices[id]).filter((d) => !!d)}
                    close={() => setState({ ...state, devicesShown: [] })}
                />
            )}
        </div>
    );
};

export const Planning = React.memo(PlanningImpl, (prevProps, nextProps) => {
    {
        const prevCamps = prevProps.campaigns.map(({ id }) => id);
        const nextCamps = nextProps.campaigns.map(({ id }) => id);

        if (!arrayEqual(prevCamps, nextCamps)) {
            return false;
        }
    }

    if (prevProps.campaigns.length !== nextProps.campaigns.length) {
        return true;
    }

    for (let i = 0; i < prevProps.campaigns.length; i++) {
        const campPrev = prevProps.campaigns[i];
        const campNext = nextProps.campaigns[i];

        if (campPrev.startDate !== campNext.startDate) {
            return false;
        }

        if (campPrev.endDate !== campNext.endDate) {
            return false;
        }

        if (campPrev.devices.length !== campNext.devices.length) {
            return false;
        }

        for (let i = 0; i < 7; i++) {
            if (!arrayEqual(campNext.planning[i], campPrev.planning[i])) {
                return false;
            }
        }

        if (campPrev.slots !== campNext.slots) {
            return false;
        }
    }

    return true;
});
